<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import Icon from 'next/icon/Icon.vue';
import CustomAttribute from 'dashboard/components/CustomAttribute.vue';
import { useAdmin } from 'dashboard/composables/useAdmin';
import EventStatusDropdown from './EventStatusDropdown.vue';
import EventLabelDropdown from './EventLabelDropdown.vue';
import EventLastChangeLine from './components/EventLastChangeLine.vue';
import { formatTime } from 'shared/helpers/formatTime';
import { resolveEventAccentColor } from './useEventStageColors';
import { getEventDateRange } from './getEventDateRange';

const props = defineProps({
  event: {
    type: Object,
    required: true,
  },
  isExpanded: {
    type: Boolean,
    default: false,
  },
  statusOptions: {
    type: Array,
    default: () => [],
  },
  eventLabels: {
    type: Array,
    default: () => [],
  },
  compact: {
    type: Boolean,
    default: false,
  },
  showLastChange: {
    type: Boolean,
    default: false,
  },
  linkQuery: {
    type: Object,
    default: null,
  },
});
const emit = defineEmits([
  'edit',
  'delete',
  'update-status',
  'update-event-label',
  'update-attribute',
  'toggle-expand',
]);
const router = useRouter();
const route = useRoute();
const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();

const accessLevel = computed(() => props.event.access_level || 'read');
const canEditEvent = computed(
  () =>
    isAdmin.value ||
    accessLevel.value === 'full' ||
    accessLevel.value === 'collaborator'
);
const canChangeStage = computed(
  () => isAdmin.value || accessLevel.value === 'full'
);
const canDeleteEvent = computed(
  () => isAdmin.value || accessLevel.value === 'full'
);
const isReadOnly = computed(() => !canEditEvent.value);

const toggleExpand = () => {
  router.push({
    name: 'planning_events_show',
    params: { eventId: props.event.id },
    query: props.linkQuery || { ...route.query, from: 'list' },
  });
};

const allocationDateRange = computed(() => getEventDateRange(props.event));

const updateStatus = statusPayload => {
  emit('update-status', {
    eventId: props.event.id,
    ...statusPayload,
  });
};

const accentColor = computed(() => resolveEventAccentColor(props.event));

const isUnread = computed(() => Boolean(props.event.unread));

const toggleReadState = async () => {
  if (isUnread.value) {
    await store.dispatch('planningEvents/markRead', props.event.id);
  } else {
    await store.dispatch('planningEvents/markUnread', props.event.id);
  }
};

// --- Computed Helpers ---

const location = computed(() => {
  if (
    props.event.allocated_resources &&
    props.event.allocated_resources.length > 0
  ) {
    return props.event.allocated_resources[0].name;
  }
  return props.event.custom_attributes?.location || 'TBA';
});
const date = computed(() => {
  const startDate = allocationDateRange.value?.start || null;
  const endDate = allocationDateRange.value?.end || null;

  if (!startDate || Number.isNaN(startDate.getTime())) return 'TBD';

  // Format start date
  const startFormatted = startDate.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'long',
  });

  // If no end date or same as start date, show single date
  if (!endDate || startDate.toDateString() === endDate.toDateString()) {
    return startFormatted;
  }

  // If different dates, show range
  const endDay = endDate.getDate();
  const endMonth = endDate.toLocaleDateString('en-US', { month: 'long' });
  const startMonth = startDate.toLocaleDateString('en-US', { month: 'long' });

  if (startMonth === endMonth) {
    // Same month: \"15-16 February\"
    return `${startDate.getDate()}-${endDay} ${endMonth}`;
  }
  // Different months: \"Feb 28 - Mar 1\"
  const startMonthShort = startDate.toLocaleDateString('en-US', {
    month: 'short',
  });
  const endMonthShort = endDate.toLocaleDateString('en-US', { month: 'short' });
  return `${startMonthShort} ${startDate.getDate()} - ${endMonthShort} ${endDay}`;
});

const time = computed(() => {
  const range = allocationDateRange.value;
  if (!range?.start || !range?.end) return 'TBA';
  if (Number.isNaN(range.start.getTime()) || Number.isNaN(range.end.getTime()))
    return 'TBA';

  // Detect full-day: starts at 00:00:00, ends at 23:59:xx on the same day
  const s = range.start;
  const e = range.end;
  if (
    s.getHours() === 0 &&
    s.getMinutes() === 0 &&
    s.getSeconds() === 0 &&
    e.getHours() === 23 &&
    e.getMinutes() === 59 &&
    e.getSeconds() >= 59 &&
    s.toDateString() === e.toDateString()
  ) {
    return t('PLANNING.EVENTS_CARD.ALL_DAY');
  }

  const format = dateObj => formatTime(dateObj);

  return `${format(range.start)} - ${format(range.end)}`;
});

const pax = computed(() => {
  const value =
    props.event.pax ??
    props.event.custom_attributes?.pax ??
    props.event.metadata?.pax;
  if (value === null || value === undefined || value === '') return null;
  return value;
});
const organization = computed(
  () =>
    props.event.customer?.name ||
    props.event.custom_attributes?.organization ||
    null
);

const contactName = computed(
  () => props.event.customer?.name || 'Unknown Contact'
);
const contactEmail = computed(() => props.event.customer?.email || 'No Email');

const eventCustomAttributes = computed(() => {
  const byCategory =
    store.getters['attributes/getEventAttributesByCategory'] || {};
  const customAttrs = props.event.custom_attributes || {};

  return Object.values(byCategory)
    .flat()
    .filter(attr => {
      if (!attr.discarded_at) return true;
      const value = customAttrs[attr.attribute_key];
      return value != null && value !== '';
    });
});

const hasCustomAttributes = computed(() => {
  return eventCustomAttributes.value.length > 0;
});

const updateCustomAttribute = async (attributeKey, value) => {
  emit('update-attribute', {
    eventId: props.event.id,
    custom_attributes: {
      ...(props.event.custom_attributes || {}),
      [attributeKey]: value,
    },
  });
};

const deleteCustomAttribute = async attributeKey => {
  const newCustomAttributes = { ...(props.event.custom_attributes || {}) };
  delete newCustomAttributes[attributeKey];

  emit('update-attribute', {
    eventId: props.event.id,
    custom_attributes: newCustomAttributes,
  });
};

const copyCustomAttribute = async value => {
  try {
    await navigator.clipboard.writeText(value);
  } catch (error) {
    console.error('Failed to copy:', error);
  }
};

// --- Trigger Reminder ---
const showReminderPanel = ref(false);
const reminderPanelReminders = ref([]);
const reminderPanelLoading = ref(false);
const triggeringReminderId = ref(null);
const sentReminderIds = ref(new Set());

const openReminderPanel = async () => {
  showReminderPanel.value = true;
  reminderPanelLoading.value = true;
  try {
    const statusType = props.event.custom_status ? 'custom' : 'system';
    const statusKey = props.event.custom_status || props.event.system_stage;
    const data = await store.dispatch('planningEventReminders/get', {
      status_type: statusType,
      status_key: statusKey,
    });
    reminderPanelReminders.value = (data || []).filter(r => r.active);
  } catch (_e) {
    reminderPanelReminders.value = [];
  } finally {
    reminderPanelLoading.value = false;
  }
};

const closeReminderPanel = () => {
  showReminderPanel.value = false;
};

const sendReminder = async reminderId => {
  triggeringReminderId.value = reminderId;
  try {
    await store.dispatch('planningEvents/triggerReminder', {
      id: props.event.id,
      reminderId,
    });
    sentReminderIds.value = new Set([...sentReminderIds.value, reminderId]);
  } finally {
    triggeringReminderId.value = null;
  }
};

const handleEscapePanel = e => {
  if (e.key === 'Escape') closeReminderPanel();
};

onMounted(() => {
  window.addEventListener('keydown', handleEscapePanel);
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleEscapePanel);
});
</script>

<template>
  <div
    class="relative bg-white dark:bg-n-solid-2 border rounded-xl border-slate-50 dark:border-n-solid-3 hover:border-slate-100 dark:hover:border-n-solid-3 hover:shadow-sm transition-all duration-200"
    :class="{ 'ring-1 ring-woot-500 border-woot-500': props.isExpanded }"
  >
    <span
      class="pointer-events-none absolute left-2 top-2.5 bottom-2.5 w-2 rounded-full"
      :style="{ backgroundColor: accentColor }"
    />
    <!-- Card Header / Summary Row -->
    <div
      class="cursor-pointer"
      :class="compact ? 'pl-6 pr-3 py-3' : 'pl-7 pr-5 py-4'"
      @click="toggleExpand"
    >
      <div
        class="flex justify-between gap-4"
        :class="compact ? 'flex-wrap items-start' : 'items-center'"
      >
        <!-- Left Section: Event Info -->
        <div class="flex items-center gap-3 flex-1 min-w-0">
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2.5 mb-2 min-w-0">
              <h3
                class="text-base font-semibold text-slate-900 dark:text-white truncate mb-0"
                :class="{ 'font-bold': isUnread }"
              >
                {{ event.name }}
              </h3>
              <div v-if="!compact" @click.stop @mousedown.stop>
                <EventLabelDropdown
                  :model-value="event.event_label_id"
                  :labels="eventLabels"
                  variant="dot"
                  :disabled="isReadOnly"
                  @update:model-value="emit('update-event-label', $event)"
                />
              </div>
              <span
                v-else-if="event.event_label"
                class="inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full bg-n-slate-3 text-xs font-semibold text-n-slate-12 max-w-[9rem] shrink-0"
              >
                <span
                  class="size-2 rounded-full flex-shrink-0"
                  :style="{ backgroundColor: event.event_label.color }"
                />
                <span class="truncate">{{ event.event_label.title }}</span>
              </span>
            </div>

            <!-- Metadata Row -->
            <div
              class="flex flex-wrap items-center text-slate-500 dark:text-slate-400"
              :class="compact ? 'gap-x-3 gap-y-1' : 'gap-x-5 gap-y-1.5'"
            >
              <div class="flex items-center gap-1.5">
                <Icon icon="i-lucide-map-pin" class="size-3.5 shrink-0" />
                <span class="text-sm">{{ location }}</span>
              </div>
              <div class="flex items-center gap-1.5">
                <Icon icon="i-lucide-calendar" class="size-3.5 shrink-0" />
                <span class="text-sm">{{ date }}</span>
              </div>
              <div class="flex items-center gap-1.5">
                <Icon icon="i-lucide-clock" class="size-3.5 shrink-0" />
                <span class="text-sm">{{ time }}</span>
              </div>

              <div v-if="pax" class="flex items-center gap-1.5">
                <Icon icon="i-lucide-users" class="size-3.5 shrink-0" />
                <span class="text-sm">{{ pax }}</span>
              </div>
              <div v-if="organization" class="flex items-center gap-1.5">
                <Icon icon="i-lucide-briefcase" class="size-3.5 shrink-0" />
                <span class="text-sm">{{ organization }}</span>
              </div>
            </div>
            <EventLastChangeLine
              v-if="showLastChange"
              class="mt-1.5"
              :last-change="event.last_change"
              :compact="compact"
            />
          </div>
        </div>

        <!-- Right Section: Actions & Status -->
        <div class="flex items-center gap-2 shrink-0">
          <EventStatusDropdown
            :model-value="event.system_stage"
            :custom-status="event.custom_status"
            :status-options="statusOptions"
            :accent-color="accentColor"
            :disabled="isReadOnly || !canChangeStage"
            size="sm"
            @update:model-value="updateStatus"
            @click.stop
          />
          <button
            v-if="!compact"
            v-tooltip.top="
              isUnread
                ? t('PLANNING.EVENTS_CARD.MARK_AS_READ')
                : t('PLANNING.EVENTS_CARD.MARK_AS_UNREAD')
            "
            type="button"
            class="shrink-0 flex items-center justify-center size-10 rounded-full"
            :class="
              isUnread
                ? 'bg-woot-50 text-woot-600'
                : 'bg-slate-50 dark:bg-slate-950 text-slate-300 hover:text-woot-600 hover:bg-woot-50'
            "
            :aria-label="
              isUnread
                ? t('PLANNING.EVENTS_CARD.UNREAD')
                : t('PLANNING.EVENTS_CARD.READ')
            "
            @click.stop="toggleReadState"
          >
            <Icon
              :icon="
                isUnread ? 'i-lucide-bell-dot animate-pulse' : 'i-lucide-bell'
              "
              class="size-5"
            />
          </button>
        </div>
      </div>
    </div>

    <!-- Expanded Details Section -->
    <div
      v-if="props.isExpanded"
      class="pl-7 pr-5 pb-5 border-t border-woot-75 dark:border-slate-800 pt-5"
    >
      <!-- Contact Information -->
      <div class="">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-x-4">
          <div class="flex items-center px-3 gap-3 h-[60px]">
            <p
              class="font-medium whitespace-nowrap text-sm mb-0 text-n-slate-12 w-full"
            >
              {{ t('PLANNING.EVENTS_CARD.CONTACT') }}
            </p>
            <p class="text-sm text-slate-900 dark:text-white w-full mb-0">
              {{ contactName }}
            </p>
            <div class="w-full" />
          </div>
          <div class="flex items-center px-3 gap-3 h-[60px]">
            <p
              class="font-medium whitespace-nowrap text-sm mb-0 text-n-slate-12 w-full"
            >
              {{ t('PLANNING.EVENTS_CARD.EMAIL') }}
            </p>
            <p class="text-sm text-slate-900 dark:text-white w-full mb-0">
              {{ contactEmail }}
            </p>
            <div class="w-full" />
          </div>
          <div
            v-if="organization"
            class="flex items-center px-3 gap-3 h-[60px]"
          >
            <p
              class="font-medium whitespace-nowrap text-sm mb-0 text-n-slate-12 w-full"
            >
              Organization
            </p>
            <p class="text-sm text-slate-900 dark:text-white w-full mb-0">
              {{ organization }}
            </p>
            <div class="w-full" />
          </div>
        </div>
      </div>

      <!-- Custom Attributes -->
      <div v-if="hasCustomAttributes">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-x-4">
          <CustomAttribute
            v-for="attribute in eventCustomAttributes"
            :key="attribute.id"
            :attribute-key="attribute.attribute_key"
            :attribute-type="attribute.attribute_display_type"
            :values="attribute.attribute_values"
            :value="event.custom_attributes?.[attribute.attribute_key]"
            :label="attribute.attribute_display_name"
            :show-actions="true"
            :read-only="isReadOnly || Boolean(attribute.discarded_at)"
            :archived="Boolean(attribute.discarded_at)"
            @update="updateCustomAttribute"
            @delete="deleteCustomAttribute"
            @copy="copyCustomAttribute"
          />
        </div>
      </div>

      <!-- Action Footer -->
      <div
        v-if="canEditEvent || canDeleteEvent"
        class="flex gap-0 mt-12 pt-4 border-t border-slate-50 dark:border-slate-800 justify-end"
      >
        <button
          v-if="canDeleteEvent"
          v-tooltip.top="$t('PLANNING.EVENTS.DELETE')"
          class="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors flex w-10 h-10 justify-center items-center"
          @click="emit('delete', event)"
        >
          <Icon icon="i-lucide-trash-2" class="size-5" />
        </button>
        <button
          v-if="canEditEvent"
          v-tooltip.top="$t('PLANNING.EVENTS.EDIT')"
          class="p-2 text-slate-400 hover:text-woot-600 hover:bg-woot-50 rounded-md transition-colors flex w-10 h-10 justify-center items-center"
          @click="emit('edit', event)"
        >
          <Icon icon="i-lucide-pen-line" class="size-5" />
        </button>

        <!-- <button
          class="p-2 text-slate-400 hover:text-woot-600 hover:bg-woot-50 rounded-md transition-colors flex w-10 h-10 justify-center items-center"
        >
          <Icon icon="i-lucide-mail" class="size-5" />
        </button>
        -->

        <!--
        <button
          class="p-2 text-slate-400 hover:text-woot-600 hover:bg-woot-50 rounded-md transition-colors flex w-10 h-10 justify-center items-center"
        >
          <Icon icon="i-lucide-calendar-check" class="size-5" />
        </button>
        -->

        <!--
        <button
          class="p-2 text-slate-400 hover:text-woot-600 hover:bg-woot-50 rounded-md transition-colors flex w-10 h-10 justify-center items-center"
        >
          <Icon icon="i-lucide-cloud-download" class="size-5" />
        </button>
        -->

        <button
          v-tooltip.top="$t('PLANNING.EVENTS.VIEW')"
          class="p-2 text-slate-400 hover:text-woot-600 hover:bg-woot-50 rounded-md transition-colors flex w-10 h-10 justify-center items-center"
          @click="emit('edit', event)"
        >
          <Icon icon="i-lucide-info" class="size-5" />
        </button>

        <!-- Trigger reminder button -->
        <div class="relative">
          <button
            v-tooltip.top="$t('PLANNING.REMINDERS.TRIGGER_NOW')"
            class="p-2 text-slate-400 hover:text-woot-600 hover:bg-woot-50 rounded-md transition-colors flex w-10 h-10 justify-center items-center"
            @click.stop="openReminderPanel"
          >
            <Icon icon="i-lucide-bell" class="size-5" />
          </button>

          <!-- Reminder panel -->
          <div
            v-if="showReminderPanel"
            class="absolute bottom-12 right-0 z-50 w-72 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg"
            @click.stop
          >
            <div class="flex items-center justify-between px-4 py-3">
              <span
                class="text-sm font-semibold text-slate-800 dark:text-slate-100"
              >
                {{ $t('PLANNING.REMINDERS.INSTANT_TITLE') }}
              </span>
              <button
                class="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                @click="closeReminderPanel"
              >
                <Icon icon="i-lucide-x" class="size-4" />
              </button>
            </div>
            <div class="p-2">
              <div
                v-if="reminderPanelLoading"
                class="pt-4 text-center text-sm text-slate-400"
              >
                {{ $t('PLANNING.REMINDERS.LOADING') }}
              </div>
              <div
                v-else-if="reminderPanelReminders.length === 0"
                class="pt-4 text-center text-sm text-slate-400"
              >
                {{ $t('PLANNING.REMINDERS.NO_REMINDERS_FOR_STATUS') }}
              </div>
              <div v-else class="flex flex-col gap-1">
                <div
                  v-for="reminder in reminderPanelReminders"
                  :key="reminder.id"
                  class="flex items-center justify-between px-3 py-2 rounded hover:bg-slate-50 dark:hover:bg-slate-800"
                >
                  <div class="flex-1 min-w-0 mr-2">
                    <p
                      class="text-xs font-medium text-slate-700 dark:text-slate-200 truncate"
                    >
                      {{
                        reminder.recipient_type === 'customer'
                          ? $t('PLANNING.REMINDERS.TO_CUSTOMER')
                          : $t('PLANNING.REMINDERS.TO_MANAGER')
                      }}
                    </p>
                    <p class="text-xs text-slate-400 truncate">
                      {{ reminder.message_template }}
                    </p>
                  </div>
                  <button
                    class="shrink-0 px-2 py-1 text-xs font-medium rounded transition-colors disabled:opacity-50"
                    :class="
                      sentReminderIds.has(reminder.id)
                        ? 'text-green-600 bg-green-50 cursor-default'
                        : 'text-woot-600 bg-woot-50 hover:bg-woot-100'
                    "
                    :disabled="
                      triggeringReminderId === reminder.id ||
                      sentReminderIds.has(reminder.id)
                    "
                    @click="sendReminder(reminder.id)"
                  >
                    <span
                      v-if="sentReminderIds.has(reminder.id)"
                      class="flex items-center gap-1"
                    >
                      <Icon icon="i-lucide-check" class="size-3" />
                      {{ $t('PLANNING.REMINDERS.SENT') }}
                    </span>
                    <span v-else>
                      {{
                        triggeringReminderId === reminder.id
                          ? $t('PLANNING.REMINDERS.SENDING')
                          : $t('PLANNING.REMINDERS.SEND')
                      }}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
